import React from 'react'

import {
  Label,
  ProgressBar,
  BodyText,
  HeadingMedium,
  HeadingSmall,
  HeadingXSmall,
} from '@pb/components'
import { getLabelFromStorage } from '@pb/utils'
import cn from 'classnames'
import { useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'
import useOptimizelyDecision from 'features/account/hooks/useOptimizelyDecision'

import cardSelectors from '../../../club-card/duck/selectors'
import { pointProgress, milestonePercentageInterval } from '../../duck/normalize'
import styles from './MemberCard.scss'

const cashbackMilestonesPoints = (
  nextTierBalance,
  bonusVoucherPointsInterval,
  pointsBalance,
): number[] => {
  let firstMilestone = 0
  let secondMilestone = bonusVoucherPointsInterval
  const milestonePoints: number[] = []

  // Check for user balance to determine the prev and next milestones
  for (let i = firstMilestone; i < nextTierBalance; i += bonusVoucherPointsInterval) {
    if (pointsBalance >= firstMilestone && pointsBalance < secondMilestone) {
      milestonePoints.push(firstMilestone, secondMilestone)
      return milestonePoints
    }
    firstMilestone = secondMilestone
    secondMilestone += bonusVoucherPointsInterval
  }
  return milestonePoints
}

const remainingPointsToReachMilestone = (milestones, pointsBalance): number => {
  const nextMilestone = milestones[milestones.length - 1] ? milestones[milestones.length - 1] : 0
  return nextMilestone - pointsBalance
}

const MemberCard: React.FC = () => {
  const firstName = useSelector(appSelectors.getUserFirstName)
  const email = useSelector(appSelectors.getUserEmail)
  const isPlusMember = useSelector(cardSelectors.getIsPlusMember)
  const memberStatus = isPlusMember
    ? getLabelFromStorage('account.level.plus')
    : getLabelFromStorage('account.level.base')
  const nextTierBalance = useSelector(cardSelectors.getNextTierBalance) || 1
  const bonusVoucherPointsInterval = useSelector(appSelectors.getBonusVoucherPointsInterval) || 1
  const pointsBalance = useSelector(cardSelectors.getCurrentBalance)
  const milestones = cashbackMilestonesPoints(
    nextTierBalance,
    bonusVoucherPointsInterval,
    pointsBalance,
  )
  const isNorthstarBrand = useOptimizelyDecision(OPTIMIZELY.ACCOUNT_NORTHSTAR_VARIATION)
  return (
    <div className={cn(styles.firstCard, isNorthstarBrand && styles.squareFirstCard)}>
      <div className={styles.heading}>
        {isNorthstarBrand ? (
          <HeadingSmall Tag="h3" className={styles.name} ellipsis>
            <Label copyKey="account.overview.salutation" replacements={firstName || email} />
          </HeadingSmall>
        ) : (
          <HeadingMedium Tag="h2" className={styles.name} ellipsis>
            <Label copyKey="account.overview.salutation" replacements={firstName || email} />
          </HeadingMedium>
        )}
        <HeadingXSmall Tag="h3" className={styles.memberType} ellipsis>
          {memberStatus}
        </HeadingXSmall>
      </div>
      <ProgressBar
        percentage={pointProgress(milestones, pointsBalance)}
        color={isNorthstarBrand ? 'favourite' : 'member'}
        thickness="medium"
        progressBarStartBullet={false}
        milestonePercentageInterval={33}
        delayInitialAnimation
        progressBarLabel="newprogress"
        intervals={milestonePercentageInterval(pointsBalance, bonusVoucherPointsInterval)}
        milestoneLabel={milestones[milestones.length - 1]}
        isMembershipCard
        isLabelVisible={false}
        currentPoint={pointsBalance}
        progressMarkerStyle={isNorthstarBrand ? 'bar' : 'round'}
      />
      <BodyText
        className={cn(
          !isNorthstarBrand
            ? styles.mediumMargin
            : pointsBalance === 0
            ? styles.mediumMargin
            : styles.largeMargin,
        )}
        lineClamp={isNorthstarBrand ? 4 : 3}
        ellipsis
      >
        <Label
          copyKey="account.vouchers.next.message"
          replacements={remainingPointsToReachMilestone(milestones, pointsBalance)}
        />{' '}
        <span>{getLabelFromStorage('account.voucher.delay.message')}</span>{' '}
        <span>{getLabelFromStorage('account.vouchers.more.info')}</span>
      </BodyText>
    </div>
  )
}
export default MemberCard
